import * as swcHelpers from '@swc/helpers';
import { Locales } from '@grandvisionhq/state';
import { getAttrValue, isDefined, getLocalizedAttrValue, getJoinedLocalizedAttrValues, getLocalizedValue } from '@grandvisionhq/utils';
import { calcPrice, priceVatCalc } from '../StructuredData/actions/helpers.js';
import { LineItemType, Category, PurchaseType, BrandType, Stock } from './types.js';

const getNameFromVariant = (variant)=>{
    const brand = getAttrValue(variant, 'brand');
    const brandModel = getAttrValue(variant, 'brandModel');
    const brandModelCode = getAttrValue(variant, 'brandModelCode');
    const name = [
        ...new Set([
            brand,
            brandModel,
            brandModelCode
        ].filter(Boolean))
    ].join(' ').trim();
    return name;
};
const getPrices = (lineItem)=>{
    var ref, ref1;
    const variant = 'variant' in lineItem ? lineItem.variant : lineItem.bundleMainLineItem.variant;
    const vat = 'variant' in lineItem ? (ref = lineItem.taxRate) === null || ref === void 0 ? void 0 : ref.amount : lineItem.bundleMainLineItem.taxRate.amount;
    let pricing;
    if (!variant) {
        console.warn('No variant data found.');
        return null;
    }
    if (typeof vat === 'undefined') {
        console.warn(`No VAT amount found for variant with SKU ${variant.sku}.`);
        return null;
    }
    if ('variant' in lineItem) {
        var _prices;
        pricing = ((_prices = variant.prices) !== null && _prices !== void 0 ? _prices : []).find((price)=>typeof price.value.centAmount !== 'undefined'
        );
    } else {
        pricing = lineItem.price;
    }
    if (!pricing) {
        console.warn(`Variant with SKU ${variant.sku} doesn't have any pricing.`);
        return null;
    }
    const regularPrice = pricing.value.centAmount;
    var _centAmount;
    const discountedPrice = (_centAmount = (ref1 = pricing.discounted) === null || ref1 === void 0 ? void 0 : ref1.value.centAmount) !== null && _centAmount !== void 0 ? _centAmount : null;
    const discountAmount = discountedPrice !== null ? regularPrice - discountedPrice : 0;
    const { vatAmount  } = discountedPrice !== null ? priceVatCalc(discountedPrice, vat) : priceVatCalc(regularPrice, vat);
    return {
        original: calcPrice(regularPrice),
        discountedPrice: discountedPrice !== null ? calcPrice(discountedPrice) : calcPrice(regularPrice),
        discountAmount: calcPrice(discountAmount),
        vatAmount: calcPrice(vatAmount)
    };
};
const getSharedAttributes = (lineItem, lineItemType, purchaseType)=>{
    var ref, ref2;
    const prices = getPrices(lineItem);
    const isBundleLineItem = 'variant' in lineItem;
    const sku = isBundleLineItem ? lineItem.variant.sku : lineItem.bundleMainLineItem.variant.sku;
    const name = isBundleLineItem ? getNameFromVariant(lineItem.variant) : '';
    var ref3, ref4;
    const erpId = isBundleLineItem ? (ref3 = getAttrValue(lineItem.variant, 'erpId')) !== null && ref3 !== void 0 ? ref3 : '' : (ref4 = getAttrValue(lineItem.bundleMainLineItem.variant, 'erpId')) !== null && ref4 !== void 0 ? ref4 : '';
    const isExclusive = isBundleLineItem ? lineItem.variant.isExclusiveBrand : lineItem.bundleMainLineItem.variant.isExclusiveBrand;
    const isOnStock = isBundleLineItem ? (ref = lineItem.variant.availability) === null || ref === void 0 ? void 0 : ref.isOnStock : (ref2 = lineItem.bundleMainLineItem.variant.availability) === null || ref2 === void 0 ? void 0 : ref2.isOnStock;
    return {
        name,
        id: sku || '',
        price: (prices === null || prices === void 0 ? void 0 : prices.discountedPrice) || '',
        dimension143: purchaseType ? purchaseType : PurchaseType.OneTime,
        dimension144: lineItemType,
        dimension145: 'true',
        dimension190: `${erpId}`,
        dimension193: isExclusive ? BrandType.OwnBrand : BrandType.ExternalBrand,
        dimension194: isOnStock ? Stock.InStock : Stock.OutOfStock,
        metric140: (prices === null || prices === void 0 ? void 0 : prices.original) || '',
        metric141: (prices === null || prices === void 0 ? void 0 : prices.discountAmount) || '',
        metric142: (prices === null || prices === void 0 ? void 0 : prices.vatAmount) || '',
        quantity: lineItem.quantity
    };
};
const getSunglassesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        category: Category.Sunglasses,
        brand,
        variant: getLocalizedAttrValue(variant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getLocalizedAttrValue(variant, 'frameColor', locale).toLowerCase(),
        dimension140: variant.sku || '',
        dimension191: getLocalizedAttrValue(variant, 'frameType', locale).toLowerCase(),
        dimension192: getLocalizedAttrValue(variant, 'faceShape', locale).toLowerCase()
    });
};
const getReadyReadersAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getNameFromVariant(lineItem.variant),
        category: Category.ReadyReaders,
        brand,
        variant: getLocalizedAttrValue(variant, 'frameShape', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'frameMaterial', locale).toLowerCase(),
        dimension147: getLocalizedAttrValue(variant, 'frameColor', locale).toLowerCase(),
        dimension191: getLocalizedAttrValue(variant, 'frameType', locale).toLowerCase(),
        dimension192: getLocalizedAttrValue(variant, 'faceShape', locale).toLowerCase()
    });
};
const getAccessoriesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const variant = lineItem.variant || [];
    const brand = getAttrValue(variant, 'brand') || '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getNameFromVariant(lineItem.variant),
        category: Category.Accessories,
        brand,
        variant: getLocalizedAttrValue(variant, 'accessoryType', locale).toLowerCase(),
        dimension16: getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase(),
        dimension146: getLocalizedAttrValue(variant, 'material', locale).toLowerCase(),
        dimension147: getJoinedLocalizedAttrValues(variant, 'accessoryColor', locale).toLowerCase(),
        dimension140: variant.sku || ''
    });
};
const getContactLensesAttributes = (locale, lineItem)=>{
    const variant = lineItem.variant;
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getLocalizedValue(lineItem.name, locale),
        brand: getAttrValue(variant, 'brand') || '',
        category: Category.ContactLenses,
        variant: getLocalizedAttrValue(variant, 'lensType', locale),
        dimension148: getLocalizedAttrValue(variant, 'replacementFrequency', locale),
        dimension146: getJoinedLocalizedAttrValues(variant, 'material', locale),
        dimension140: variant.sku || ''
    });
};
const getPrescriptionGlassesAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Bundle);
    const category = lineItem.bundleMainLineItem.productType === 'sunglasses' ? Category.Sunglasses : Category.Optical;
    const { variant , brand , brandModel , brandModelCode  } = lineItem.bundleMainLineItem;
    const name = [
        ...new Set([
            brand,
            brandModel,
            brandModelCode
        ].filter(Boolean))
    ].join(' ');
    const frameMaterial = getJoinedLocalizedAttrValues(variant, 'frameMaterial', locale).toLowerCase();
    const frameColor = getJoinedLocalizedAttrValues(variant, 'frameColor', locale).toLowerCase();
    const frameShape = getJoinedLocalizedAttrValues(variant, 'frameShape', locale).toLowerCase();
    const gender = getJoinedLocalizedAttrValues(variant, 'gender', locale).toLowerCase();
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name,
        brand: lineItem.bundleMainLineItem.brand || '',
        category,
        variant: frameShape,
        dimension140: variant.sku || ''
    }, frameMaterial ? {
        dimension146: frameMaterial
    } : null, frameColor ? {
        dimension147: frameColor
    } : null, gender ? {
        dimension16: gender
    } : null, {
        dimension191: getLocalizedAttrValue(variant, 'frameType', locale).toLowerCase(),
        dimension192: getLocalizedAttrValue(variant, 'faceShape', locale).toLowerCase()
    });
};
const getSolutionsAttributes = (locale, lineItem)=>{
    const sharedAttributes = getSharedAttributes(lineItem, LineItemType.Single);
    const brand = getAttrValue(lineItem.variant, 'brand') ? String(getAttrValue(lineItem.variant, 'brand')) : '';
    return swcHelpers.objectSpread({}, sharedAttributes, {
        name: getLocalizedValue(lineItem.name, locale),
        category: Category.Solutions,
        brand,
        variant: 'solution'
    });
};
/**
 *  Mappers for GQL Order line item data to product data for gvPurchaseEvent
 */ const transformValue = (data, locale)=>data.filter((item)=>item[locale] || item[locale.substring(0, 2)]
    ).reduce((_, next)=>{
        const localeValue = next[locale];
        const fallbackValue = next[locale.substring(0, 2)];
        return localeValue || fallbackValue || '';
    }, '')
;
const formatAttribute = (attributes)=>({
        [attributes.locale]: attributes.value
    })
;
const formatAttributes = (attributes)=>({
        [attributes.locale]: attributes.values.join(',')
    })
;
const transformToEventPayload = ({ dimension147 , dimension16 , dimension146 , variant , locale  })=>{
    return {
        variant: variant ? transformValue(variant, locale) : '',
        dimension16: dimension16 ? transformValue(dimension16, locale).toLowerCase() : '',
        dimension146: dimension146 ? transformValue(dimension146, locale) : '',
        dimension147: dimension147 ? transformValue(dimension147, locale) : ''
    };
};
// Data in the Data Layer should always be English, therefore hardcoded locale
const bindLocaleToMapper = (mapper)=>mapper.bind(null, Locales['en-GB'])
;
// Mapping for legacy line item product type
const lineItemDataLayerMapperTable = {
    Sunglasses: bindLocaleToMapper(getSunglassesAttributes),
    'Contact-lenses': bindLocaleToMapper(getContactLensesAttributes),
    Solutions: bindLocaleToMapper(getSolutionsAttributes),
    'Ready-readers': bindLocaleToMapper(getReadyReadersAttributes),
    Accessories: bindLocaleToMapper(getAccessoriesAttributes)
};
const lineItemToProductItem = (lineItem)=>{
    var ref;
    const productType = (ref = lineItem.productType.obj) === null || ref === void 0 ? void 0 : ref.name;
    if (!productType) {
        return;
    }
    const mapper = lineItemDataLayerMapperTable[productType];
    if (!mapper) {
        return;
    }
    return mapper(lineItem);
};
const mapLineItemToDataLayerProductObjects = (lineItems, prescriptionGlassesLineItems)=>{
    const productObjects = [];
    if (lineItems) {
        productObjects.push(...lineItems.map(lineItemToProductItem).filter(isDefined));
    }
    if (prescriptionGlassesLineItems) {
        productObjects.push(...prescriptionGlassesLineItems.map(getPrescriptionGlassesAttributes.bind(null, Locales['en-GB'])).filter(isDefined));
    }
    return productObjects;
};

export { formatAttribute, formatAttributes, getNameFromVariant, mapLineItemToDataLayerProductObjects, transformToEventPayload, transformValue };
//# sourceMappingURL=utils.js.map
